import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { storeService } from '../../api/services.js';
import { useCart } from '../../contexts/CartContext';
import AddToCartPopup from './AddToCartPopup';

const Section = styled.section``;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  color: ${props => props.theme.colors.primary.text};
  opacity: 0.7;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  color: ${props => props.theme.colors.accent.main};
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.md};
  }
`;

const ProductCard = styled.div`
  background: ${props => props.theme.colors.primary.background};
  border: 1px solid ${props => props.theme.colors.neutral.light};
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  opacity: ${props => props.$isOutOfStockInCart ? 0.5 : 1};
  
  &:hover {
    transform: ${props => props.$isOutOfStockInCart ? 'none' : 'translateY(-4px)'};
    box-shadow: ${props => props.$isOutOfStockInCart ? 'none' : '0 10px 30px ' + props.theme.shadows.medium};
  }
`;

const ProductImageContainer = styled.div`
  width: 100%;
  height: 200px;
  background: ${props => props.theme.colors.neutral.lightest};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProductImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: ${props => props.theme.colors.neutral.light};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary.text};
  opacity: 0.3;
  font-size: 3rem;
`;

const StockBadge = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing.sm};
  right: ${props => props.theme.spacing.sm};
  background: ${props => props.$inStock ? props.theme.colors.accent.bright : props.theme.colors.neutral.medium};
  color: ${props => props.$inStock ? props.theme.colors.accent.main : props.theme.colors.primary.background};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProductContent = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

const ProductHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ProductName = styled.h3`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary.text};
  margin: 0 0 ${props => props.theme.spacing.xs} 0;
`;

const ProductCategory = styled.div`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${props => props.theme.colors.primary.text};
  opacity: 0.6;
  text-transform: capitalize;
`;

const ProductDescription = styled.p`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme.colors.primary.text};
  line-height: 1.5;
  margin-bottom: ${props => props.theme.spacing.md};
  opacity: 0.8;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ProductFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.neutral.light};
`;

const ProductPrice = styled.div`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.accent.main};
`;

const AddToCartButton = styled.button`
  font-family: ${props => props.theme.typography.button.fontFamily};
  font-size: ${props => props.theme.typography.button.fontSize};
  font-weight: ${props => props.theme.typography.button.fontWeight};
  letter-spacing: ${props => props.theme.typography.button.letterSpacing};
  text-transform: ${props => props.theme.typography.button.textTransform};
  
  background: transparent;
  color: ${props => props.theme.colors.primary.buttons};
  border: 1px solid ${props => props.theme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  text-decoration: none;
  
  &:hover {
    background: ${props => props.theme.colors.primary.buttons};
    color: ${props => props.theme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background: transparent;
      color: ${props => props.theme.colors.primary.buttons};
      transform: none;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.md};
  
  p {
    font-family: ${props => props.theme.typography.secondary.fontFamily};
    font-size: 1.1rem;
    color: ${props => props.theme.colors.primary.text};
    opacity: 0.7;
    margin: 0;
  }
`;

const ShopSection = () => {
  const [storeItems, setStoreItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddToCart, setShowAddToCart] = useState(false);
  const { cartItems } = useCart();

  useEffect(() => {
    loadStoreItems();
  }, []);

  const loadStoreItems = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch store items from API
      const response = await storeService.getStoreItems({ inStockOnly: false });
      const items = response.data || response || [];
      
      setStoreItems(items);
    } catch (err) {
      console.error('Failed to load store items:', err);
      setError('Failed to load store items. Please try again later.');
      setStoreItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setShowAddToCart(true);
  };

  const isItemInCart = (item) => {
    return cartItems.some(cartItem => 
      cartItem.itemType === 'store' && cartItem.name === item.name
    );
  };

  const formatItemForCart = (item) => {
    const inventoryCount = item.total_inventory ?? item.inventory_count ?? item.inventory ?? item.stock ?? item.quantity ?? item.available_quantity;
    // Also check for boolean flags
    const inStockFlag = item.in_stock ?? item.is_available ?? item.available;
    
    // Priority: boolean flags first, then inventory count, then default to in-stock
    const isInStock = inStockFlag !== undefined ? inStockFlag : 
                     (inventoryCount !== undefined ? inventoryCount > 0 : true);
    
    return {
      ...item,
      price: item.price || 0,
      name: item.name || 'Unknown Item',
      description: item.description || '',
      category: item.category || 'store',
      inventory: inventoryCount,
      inStock: isInStock
    };
  };

  if (loading) {
    return (
      <Section>
        <LoadingMessage>Loading store items...</LoadingMessage>
      </Section>
    );
  }

  if (error) {
    return (
      <Section>
        <ErrorMessage>{error}</ErrorMessage>
      </Section>
    );
  }

  if (storeItems.length === 0) {
    return (
      <Section>
        <EmptyState>
          <p>No products available at the moment.</p>
          <p>Check back soon for new items!</p>
        </EmptyState>
      </Section>
    );
  }

  return (
    <Section>
      <ProductGrid>
        {storeItems.map((item, index) => {
          
          // Try multiple possible inventory fields from the API
          const inventoryCount = item.total_inventory ?? item.inventory_count ?? item.inventory ?? item.stock ?? item.quantity ?? item.available_quantity;
          // Also check for boolean flags
          const inStockFlag = item.in_stock ?? item.is_available ?? item.available;
          
          // Priority: boolean flags first, then inventory count, then default to in-stock
          const isInStock = inStockFlag !== undefined ? inStockFlag : 
                           (inventoryCount !== undefined ? inventoryCount > 0 : true);
          const itemInCart = isItemInCart(item);
          const isOutOfStockInCart = !isInStock && itemInCart;
          
          return (
            <ProductCard
              key={item.id || index}
              onClick={() => handleItemClick(item)}
              $isOutOfStockInCart={isOutOfStockInCart}
            >
              <ProductImageContainer>
                {item.image_url ? (
                  <ProductImage 
                    src={item.image_url} 
                    alt={item.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <ProductImagePlaceholder style={{ display: item.image_url ? 'none' : 'flex' }}>
                  📦
                </ProductImagePlaceholder>
                {!isInStock && (
                  <StockBadge $inStock={isInStock}>
                    Out of Stock
                  </StockBadge>
                )}
              </ProductImageContainer>
              
              <ProductContent>
                <ProductHeader>
                  <ProductName>{item.name}</ProductName>
                  <ProductCategory>{item.category}</ProductCategory>
                </ProductHeader>
                
                <ProductDescription>
                  {item.description}
                </ProductDescription>
                
                <ProductFooter>
                  <ProductPrice>${item.price}</ProductPrice>
                  <AddToCartButton 
                    disabled={!isInStock}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(item);
                    }}
                  >
                    {isInStock ? 'Add to Cart' : 'Out of Stock'}
                  </AddToCartButton>
                </ProductFooter>
              </ProductContent>
            </ProductCard>
          );
        })}
      </ProductGrid>

      {selectedItem && (
        <AddToCartPopup
          isOpen={showAddToCart}
          onClose={() => {
            setShowAddToCart(false);
            setSelectedItem(null);
          }}
          item={formatItemForCart(selectedItem)}
          itemType="store"
        />
      )}
    </Section>
  );
};

export default ShopSection;